import { createContext, useState, useContext, useRef, useCallback, useEffect } from "react";

const NavigationHistoryContext = createContext();
const MAX_HISTORY = 10;

// Routes that should be excluded from history
const EXCLUDED_ROUTES = [
  "/auth", // Login page
];

export function NavigationHistoryProvider({ children }) {
  // Use sessionStorage for persistence across page refreshes
  const [history, setHistory] = useState(() => {
    // Load history from sessionStorage if available
    const savedHistory = sessionStorage.getItem("navigationHistory");
    if (savedHistory) {
      const parsed = JSON.parse(savedHistory);
      // Convert legacy string entries to objects for backward compatibility
      return parsed.map((item) => (typeof item === "string" ? { url: item, options: {} } : item));
    }
    return [];
  });

  // Flag to indicate we're in the process of navigating back
  const isNavigatingBack = useRef(false);

  // Flag to indicate we're in the process of updating programmatically
  const isUpdate = useRef(false);

  // Function to add a path to history
  const addToHistory = (pathname, search = "") => {
    // Skip excluded routes
    if (EXCLUDED_ROUTES.some((route) => pathname === route)) {
      return;
    }

    // If we're navigating back, don't add to history and reset the flag
    if (isNavigatingBack.current) {
      isNavigatingBack.current = false;
      return;
    }

    // If we're updating programmatically, don't add to history and reset the flag
    if (isUpdate.current) {
      isUpdate.current = false;
      return;
    }

    const fullPath = `${pathname}${search}`;
    const historyItem = { url: fullPath, options: {} };
    const currentItem = history.length > 0 ? history[history.length - 1] : null;

    // Don't record the same route twice in a row
    if (history.length === 0 || currentItem?.url !== fullPath) {
      // Create a new history array with the new path
      const newHistory = [...history, historyItem];

      // Keep only the last MAX_HISTORY items
      const trimmedHistory = newHistory.slice(-MAX_HISTORY);

      // Update state directly
      setHistory(trimmedHistory);

      // Persist to sessionStorage
      sessionStorage.setItem("navigationHistory", JSON.stringify(trimmedHistory));
    }
  };

  // Function to add a path with options to history
  const addToHistoryWithOptions = (url, options = {}) => {
    // Skip excluded routes
    if (EXCLUDED_ROUTES.some((route) => url.startsWith(route))) {
      return;
    }

    // If we're navigating back, don't add to history and reset the flag
    if (isNavigatingBack.current) {
      isNavigatingBack.current = false;
      return;
    }

    // If we're updating programmatically, don't add to history and reset the flag
    if (isUpdate.current) {
      isUpdate.current = false;
      return;
    }

    const sanitizedOptions = sanitizeOptionsForStorage(options);
    const historyItem = { url, options: sanitizedOptions };
    const currentItem = history.length > 0 ? history[history.length - 1] : null;

    // Don't record the same route twice in a row
    if (history.length === 0 || currentItem?.url !== url) {
      // Create a new history array with the new path
      const newHistory = [...history, historyItem];

      // Keep only the last MAX_HISTORY items
      const trimmedHistory = newHistory.slice(-MAX_HISTORY);

      // Update state directly
      setHistory(trimmedHistory);

      // Persist to sessionStorage
      sessionStorage.setItem("navigationHistory", JSON.stringify(trimmedHistory));
    }
  };

  // Function to check if we can go back
  const canGoBack = () => {
    return history.length > 1;
  };

  // Function to update history when going back
  const goBack = () => {
    if (history.length > 1) {
      // Create a new history array without the last entry
      const newHistory = [...history];

      // Remove the current page (last item) from the array
      newHistory.pop();

      // Update state directly
      setHistory(newHistory);

      // Persist to sessionStorage
      sessionStorage.setItem("navigationHistory", JSON.stringify(newHistory));

      // Set the flag to indicate we're navigating back
      isNavigatingBack.current = true;

      return true;
    }
    return false;
  };

  // Function to get the previous URL
  const getPreviousUrl = () => {
    return history.length > 1 ? history[history.length - 2].url : "/";
  };

  // Function to update the latest (last) item in history with options
  const updateLatestHistoryItemWithOptions = (url, options = {}) => {
    if (history.length === 0) {
      return false; // No items to update
    }

    // Set the flag to indicate we're updating programmatically
    isUpdate.current = true;

    // Create a new history array with the last item updated
    const sanitizedOptions = sanitizeOptionsForStorage(options);
    const newHistory = [...history];
    newHistory[newHistory.length - 1] = { url, options: sanitizedOptions };

    // Update state
    setHistory(newHistory);

    // Persist to sessionStorage
    sessionStorage.setItem("navigationHistory", JSON.stringify(newHistory));

    return true;
  };

  // Function to get options for the current (latest) history entry
  const getCurrentHistoryOptions = () => {
    if (history.length === 0) {
      return {}; // No history entries
    }

    const latestItem = history[history.length - 1];
    const currentUrl = window.location.pathname + window.location.search;

    // Handle legacy string entries
    if (typeof latestItem === "string") {
      return {}; // Legacy entry has no options
    }

    // This prevents cross-page filter contamination while preserving back navigation
    if (latestItem.url !== currentUrl) {
      return {};
    }

    return latestItem.options || {};
  };

  // Function to update scroll position in current history item
  const updateScrollPosition = useCallback(() => {
    const currentOptions = getCurrentHistoryOptions();
    const scrollY = window.scrollY;

    updateLatestHistoryItemWithOptions(window.location.pathname + window.location.search, {
      ...currentOptions,
      scrollY,
    });
  }, [getCurrentHistoryOptions, updateLatestHistoryItemWithOptions]);

  // Function to restore scroll position from options
  const restoreScrollY = useCallback((options = {}) => {
    if (options.scrollY !== undefined) {
      window.scrollTo(0, options.scrollY);
    }
  }, []);

  // Auto scroll management hook
  const useAutoScrollManagement = useCallback(
    (loading = false, delay = 100) => {
      // Auto-restore when loading completes
      useEffect(() => {
        if (!loading) {
          const timeoutId = setTimeout(() => {
            const options = getCurrentHistoryOptions();
            restoreScrollY(options);
          }, delay);

          return () => clearTimeout(timeoutId);
        }
      }, [loading, delay, getCurrentHistoryOptions, restoreScrollY]);

      // Auto-track scroll changes
      useEffect(() => {
        let timeoutId;

        const handleScroll = () => {
          if (timeoutId) clearTimeout(timeoutId);
          timeoutId = setTimeout(() => updateScrollPosition(), 300);
        };

        window.addEventListener("scroll", handleScroll, { passive: true });

        return () => {
          window.removeEventListener("scroll", handleScroll);
          if (timeoutId) clearTimeout(timeoutId);
        };
      }, [updateScrollPosition]);

      return { updateScrollPosition, restoreScrollY };
    },
    [getCurrentHistoryOptions, restoreScrollY, updateScrollPosition]
  );

  return (
    <NavigationHistoryContext.Provider
      value={{
        history,
        addToHistory,
        addToHistoryWithOptions,
        goBack,
        canGoBack,
        getPreviousUrl,
        updateLatestHistoryItemWithOptions,
        getCurrentHistoryOptions,
        updateScrollPosition,
        restoreScrollY,
        useAutoScrollManagement,
        isNavigatingBack: isNavigatingBack.current,
      }}
    >
      {children}
    </NavigationHistoryContext.Provider>
  );
}

// Helper function to convert date/time objects to ISO strings for safe storage
const sanitizeOptionsForStorage = (options) => {
  if (options === null || options === undefined) {
    return options;
  }

  // Handle arrays
  if (Array.isArray(options)) {
    return options.map((item) => sanitizeOptionsForStorage(item));
  }

  // Handle objects
  if (typeof options === "object") {
    // Check if it's a Luxon DateTime object
    if (options.isLuxonDateTime) {
      return options.toISO();
    }

    // Check if it's a native Date object
    if (options instanceof Date) {
      return options.toISOString();
    }

    // Check if it's a Firestore Timestamp
    if (options.seconds !== undefined && options.nanoseconds !== undefined && typeof options.toDate === "function") {
      return options.toDate().toISOString();
    }

    // Recursively process nested objects
    const sanitized = {};
    for (const [key, value] of Object.entries(options)) {
      sanitized[key] = sanitizeOptionsForStorage(value);
    }
    return sanitized;
  }

  // Return primitives as-is (strings, numbers, booleans)
  return options;
};

export function useNavigationHistory() {
  return useContext(NavigationHistoryContext);
}
